import { useParams, Link } from "react-router-dom";
import { MapPin, ArrowLeft, Leaf, CheckCircle2, ExternalLink } from "lucide-react";
import { StarRow } from "../components/Pill";
import { PROVIDERS } from "../mock";

export default function ProviderDetail() {
  const { slug } = useParams();
  const p = PROVIDERS.find((x) => x.slug === slug);

  if (!p) {
    return (
      <main className="mx-auto max-w-[1200px] px-5 py-24 text-center lg:px-8">
        <h1 className="font-display text-[28px] font-extrabold text-[var(--ink)]">Provider not found</h1>
        <Link to="/providers" className="mt-4 inline-block font-semibold text-[var(--brand-blue)]">Back to directory</Link>
      </main>
    );
  }

  return (
    <main className="bg-[#f7f8fa]">
      <div className="mx-auto max-w-[860px] px-5 py-12 lg:px-8 lg:py-16">
        <Link to="/providers" className="inline-flex items-center gap-2 text-[14px] font-semibold text-[var(--brand-blue)] hover:text-[var(--brand-blue-hover)]">
          <ArrowLeft size={16} /> Back to directory
        </Link>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-8 lg:p-10">
          <div className="flex items-start gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[var(--navy)] font-display text-[18px] font-extrabold text-white">
              {p.mono}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-3">
                {p.editor && (
                  <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-600">Editor's Choice</span>
                )}
                {p.green && (
                  <a href="#" onClick={(e) => e.preventDefault()} className="inline-flex items-center gap-1 text-[13px] font-semibold text-emerald-600 hover:text-emerald-700">
                    Green Certified <ExternalLink size={13} />
                  </a>
                )}
              </div>
              <h1 className="font-display mt-2 text-[32px] font-extrabold leading-tight tracking-tight text-[var(--ink)] sm:text-[38px]">{p.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-[14px] text-slate-500">
                <span className="flex items-center gap-1.5">
                  <StarRow n={p.stars} size={15} />
                  {p.reviews != null ? (
                    <><span className="ml-1 font-bold text-[var(--ink)]">{p.rating}</span> · {p.reviews} reviews</>
                  ) : (
                    <span>No Google Reviews</span>
                  )}
                </span>
                <span className="flex items-center gap-1.5"><MapPin size={15} className="text-slate-400" /> {p.location}</span>
              </div>
            </div>
          </div>

          <hr className="my-7 border-slate-100" />

          <h2 className="font-display text-[22px] font-bold text-[var(--ink)]">Overview</h2>
          <p className="mt-3 text-[16px] leading-relaxed text-slate-500">{p.overview}</p>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {p.tags.map((t, i) => (
              <span key={i} className="rounded-md bg-slate-100 px-2.5 py-1 text-[12px] font-medium text-slate-600">{t}</span>
            ))}
          </div>

          <div className="mt-8 rounded-2xl bg-slate-50 p-6">
            <p className="eyebrow">Evaluation Notes</p>
            <h3 className="font-display mt-1 text-[19px] font-bold text-[var(--ink)]">Why this listing</h3>
            <ul className="mt-4 space-y-3">
              {p.notes.map((n, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px] text-slate-600">
                  <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-500" /> {n}
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-6 text-[13px] leading-relaxed text-slate-400">
            Technology summaries are generalized and may not reflect every service offering of this company. Verify specific services directly with the provider before booking.
          </p>
        </div>
      </div>
    </main>
  );
}
